import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import AddressAutocomplete from '../common/AddressAutocomplete';
import localeConfig from '../../config/locale.config';

const VendorLocationsForm = ({ data, onNext, onPrevious, onSave, loading, colors, isPending, registrationStatus }) => {
    const [locations, setLocations] = useState([]);
    const [expandedLocations, setExpandedLocations] = useState(new Set([])); // Keep first location collapsed by default

    useEffect(() => {
        if (data.locations && data.locations.length > 0) {
            setLocations(data.locations);
            // Keep first location collapsed by default
            setExpandedLocations(new Set([]));
        } else {
            // Add at least one location with proper structure
            setLocations([{
                // Location details
                location_address: {
                    address: '',
                    state: '',
                    city: '',
                    latitude: null,
                    longitude: null,
                    pincode: ''
                },
                operating_hours: {
                    monday: { open: '06:00', close: '18:00' },
                    tuesday: { open: '06:00', close: '18:00' },
                    wednesday: { open: '06:00', close: '18:00' },
                    thursday: { open: '06:00', close: '18:00' },
                    friday: { open: '06:00', close: '18:00' },
                    saturday: { open: '07:00', close: '16:00' },
                    sunday: { open: '08:00', close: '14:00' }
                },
                vendor_location_type: 'VENDOR_ONLY',
                            // Manager user details (will be created as VENDOR_MANAGER)
                manager_name: '',
                manager_mobile: '',
                manager_email: ''
            }]);
            // Keep first location collapsed by default
            setExpandedLocations(new Set([]));
        }
    }, [data.locations]);

    const toggleLocationExpansion = (index) => {
        const newExpanded = new Set(expandedLocations);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedLocations(newExpanded);
    };

    const handleLocationChange = (index, field, value) => {
        const updatedLocations = [...locations];
        updatedLocations[index] = {
            ...updatedLocations[index],
            [field]: value
        };
        setLocations(updatedLocations);
    };

    const handleAddressSelect = (index, addressData) => {
        const updatedLocations = [...locations];
        updatedLocations[index] = {
            ...updatedLocations[index],
            location_address: {
                address: addressData.address,
                state: addressData.state,
                city: addressData.city,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                pincode: addressData.pincode
            }
        };
        setLocations(updatedLocations);
    };

    const handleAddressChange = (index, value) => {
        const updatedLocations = [...locations];
        updatedLocations[index] = {
            ...updatedLocations[index],
            location_address: {
                ...updatedLocations[index].location_address,
                address: value
            }
        };
        setLocations(updatedLocations);
    };

    const handleOperatingHoursChange = (index, day, field, value) => {
        const updatedLocations = [...locations];
        updatedLocations[index] = {
            ...updatedLocations[index],
            operating_hours: {
                ...updatedLocations[index].operating_hours,
                [day]: {
                    ...updatedLocations[index].operating_hours[day],
                    [field]: value
                }
            }
        };
        setLocations(updatedLocations);
    };

    const addLocation = () => {
        const newIndex = locations.length;
        setLocations([...locations, {
            location_address: {
                address: '',
                state: '',
                city: '',
                latitude: null,
                longitude: null,
                pincode: ''
            },
            operating_hours: {
                monday: { open: '06:00', close: '18:00' },
                tuesday: { open: '06:00', close: '18:00' },
                wednesday: { open: '06:00', close: '18:00' },
                thursday: { open: '06:00', close: '18:00' },
                friday: { open: '06:00', close: '18:00' },
                saturday: { open: '07:00', close: '16:00' },
                sunday: { open: '08:00', close: '14:00' }
            },
            vendor_location_type: 'VENDOR_ONLY',
            // Manager user details (will be created as VENDOR_MANAGER)
            manager_name: '',
            manager_mobile: '',
            manager_email: ''
        }]);
        // Auto-expand the new location
        setExpandedLocations(new Set([...expandedLocations, newIndex]));
    };

    const removeLocation = (index) => {
        if (locations.length > 1) {
            const updatedLocations = locations.filter((_, i) => i !== index);
            setLocations(updatedLocations);
            
            // Update expanded locations
            const newExpanded = new Set();
            expandedLocations.forEach(expandedIndex => {
                if (expandedIndex < index) {
                    newExpanded.add(expandedIndex);
                } else if (expandedIndex > index) {
                    newExpanded.add(expandedIndex - 1);
                }
            });
            setExpandedLocations(newExpanded);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prevent submission if registration is pending
        if (isPending) {
            return;
        }
        
        // Basic validation
        const hasValidLocation = locations.some(loc => 
            loc.location_address.address && 
            loc.manager_name && 
            loc.manager_mobile
        );
        
        if (!hasValidLocation) {
            alert('Please fill in all required fields for at least one location');
            return;
        }

        onNext({ locations });
    };

    const handleSave = () => {
        // Prevent saving if registration is pending
        if (isPending) {
            onSave({});
            return;
        }

        // Basic validation for save
        const hasValidLocation = locations.some(loc => 
            loc.location_address.address && 
            loc.manager_name && 
            loc.manager_mobile
        );
        
        if (!hasValidLocation) {
            alert('Please fill in all required fields for at least one location before saving');
            return;
        }

        onSave({ locations });
    };

    const formatMobileNumber = (value) => {
        const digits = value.replace(/\D/g, '');
        return digits.slice(0, 10);
    };

    const days = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' }
    ];

    const getLocationSummary = (location) => {
        const address = location.location_address.address || 'No address';
        const manager = location.manager_name || 'No manager';
        return `${address} - ${manager}`;
    };

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Vendor Locations</h2>
                <p className="text-sm text-gray-600">Add the locations where your vendor business operates.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Location Cards */}
                <div className="space-y-3">
                    {locations.map((location, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Location Header */}
                            <div 
                                className="bg-blue-50 px-4 py-2.5 cursor-pointer hover:bg-blue-100 transition-colors border-b border-blue-100"
                                onClick={() => toggleLocationExpansion(index)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {expandedLocations.has(index) ? (
                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 text-gray-500" />
                                        )}
                                        <div className="flex items-center space-x-3">
                                            <h3 className="text-base font-medium text-gray-900">
                                                Location {index + 1}
                                            </h3>
                                            <span className="text-xs text-gray-500">
                                                {location.vendor_location_type.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600 max-w-xs truncate">
                                            {getLocationSummary(location)}
                                        </span>
                                        {locations.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeLocation(index);
                                                }}
                                                className="text-red-600 hover:text-red-800 p-1"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Location Content */}
                            {expandedLocations.has(index) && (
                                <div className="p-4 bg-white">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Location Type */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Location Type *
                                            </label>
                                            <select
                                                value={location.vendor_location_type}
                                                onChange={(e) => handleLocationChange(index, 'vendor_location_type', e.target.value)}
                                                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                disabled={isPending}
                                            >
                                                <option value="VENDOR_ONLY">Vendor Only</option>
                                                <option value="VENDOR_AND_DEALER">Vendor & Dealer</option>
                                                <option value="DEALER_PARTNER">Dealer Partner</option>
                                            </select>
                                        </div>

                                        {/* Address */}
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Location Address *
                                            </label>
                                            <AddressAutocomplete
                                                value={location.location_address.address}
                                                onChange={(value) => handleAddressChange(index, value)}
                                                onAddressSelect={(addressData) => handleAddressSelect(index, addressData)}
                                                placeholder="Search for location address"
                                                disabled={isPending}
                                            />
                                        </div>

                                        {/* Manager Information Section */}
                                        <div className="md:col-span-2">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Location Manager</h4>
                                        </div>

                                        {/* Manager Name */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Manager Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={location.manager_name}
                                                onChange={(e) => handleLocationChange(index, 'manager_name', e.target.value)}
                                                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                placeholder="Enter manager name"
                                                required
                                                disabled={isPending}
                                            />
                                        </div>



                                        {/* Manager Mobile */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Manager Mobile *
                                            </label>
                                            <input
                                                type="tel"
                                                value={location.manager_mobile}
                                                onChange={(e) => handleLocationChange(index, 'manager_mobile', formatMobileNumber(e.target.value))}
                                                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                placeholder={localeConfig.mobile.placeholder}
                                                required
                                                disabled={isPending}
                                            />
                                        </div>

                                        {/* Manager Email */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Manager Email
                                            </label>
                                            <input
                                                type="email"
                                                value={location.manager_email}
                                                onChange={(e) => handleLocationChange(index, 'manager_email', e.target.value)}
                                                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                placeholder="Enter email address"
                                                disabled={isPending}
                                            />
                                        </div>

                                        {/* Operating Hours Section */}
                                        <div className="md:col-span-2">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Operating Hours</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {days.map(day => (
                                                    <div key={day.key} className="flex items-center space-x-2">
                                                        <label className="block text-xs font-medium text-gray-700 w-16">
                                                            {day.label}
                                                        </label>
                                                        <input
                                                            type="time"
                                                            value={location.operating_hours[day.key].open}
                                                            onChange={(e) => handleOperatingHoursChange(index, day.key, 'open', e.target.value)}
                                                            className={`flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                            disabled={isPending}
                                                        />
                                                        <span className="text-xs text-gray-500">to</span>
                                                        <input
                                                            type="time"
                                                            value={location.operating_hours[day.key].close}
                                                            onChange={(e) => handleOperatingHoursChange(index, day.key, 'close', e.target.value)}
                                                            className={`flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                                            disabled={isPending}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Add Location Button */}
                {!isPending && (
                    <button
                        type="button"
                        onClick={addLocation}
                        className="flex items-center space-x-2 px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Add Another Location</span>
                    </button>
                )}

                {/* Navigation Buttons */}
                {!isPending && (
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onPrevious}
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            <span>Previous</span>
                        </button>

                        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span>Save & Exit</span>
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 sm:py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            <span>{loading ? 'Saving...' : 'Next'}</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
                )}
            </form>
        </div>
    );
};

export default VendorLocationsForm; 